import { IClient } from "@sp-crm/core";
import { InputFormBlock } from "components/app/input-form-block";
import { Checkbox } from "components/ui/checkbox";
import { fancyConfirm } from "components/ui/fancy-confirm";
import { SecondaryButton } from "components/ui/secondary-button";
import React, { FunctionComponent } from "react";
import { useNow } from "store/selectors/hooks";
import { useTenantSettings } from "store/selectors/settings";
import AdditionalClientInfo from "./additional-client-info";
import ClientInfoHeader from "./header";
import PrimaryClientInfo from "./primary-client-info";

type ClientInfoFormProps = {
    client: IClient;
    onFieldChange: (fieldName: string, newValue: unknown) => void;
    onMultiFieldChange: (newValues: Record<string, unknown>) => void;
};

const CONFIRM_DATA_OVERWRITE_TEXT = `This will copy the Best Contact person's details.
    It will overwrite at least some of "Client's" details that
    you've already typed below (this may be exactly what you want
    to do!).`;

export const ClientInfoForm: FunctionComponent<ClientInfoFormProps> = props => {
    const { client, onFieldChange, onMultiFieldChange } = props;
    const now = useNow();

    const { settings } = useTenantSettings();

    const sameAsBestContactCheckboxChanged = (newValue: boolean) => {
        if (!newValue) {
            onFieldChange("clientSameAsBestContact", newValue);
        } else {
            (async () => {
                const result = await fancyConfirm(
                    "Copy Best Contact Info?",
                    CONFIRM_DATA_OVERWRITE_TEXT,
                    "Yes, I'm sure",
                    "Cancel",
                );
                if (result) {
                    onMultiFieldChange({
                        name: client.bestContactEntity.name,
                        cellPhone: client.bestContactEntity.cellPhone,
                        phone: client.bestContactEntity.phone1,
                        email: client.bestContactEntity.email1,
                        emailOptOut: client.bestContactEntity.email1OptOut,
                        address: client.bestContactEntity.address1,
                        city: client.bestContactEntity.city,
                        state: client.bestContactEntity.state,
                        zip: client.bestContactEntity.zip,
                        clientSameAsBestContact: true,
                    });
                }
            })();
        }
    };

    const onCreateNewAdditionalClient = () => onFieldChange("additionalClient", {});

    const onDeleteAdditionalClient = async () => {
        const result = await fancyConfirm(
            "Delete additional client?",
            "This will delete all the additional client data",
            "Yes, I'm sure",
            "Cancel",
        );
        if (result) {
            onFieldChange("additionalClient", null);
        }
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
    const onAdditionalClientFieldChange = (fieldName: string, newValue: any) => {
        onFieldChange(`additionalClient.${fieldName}`, newValue);
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
    const onAdditionalClientMultiFieldChange = (newValues: any) => {
        const additionalClientNewValues = {};
        for (const [key, value] of Object.entries(newValues)) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
            (additionalClientNewValues as any)[`additionalClient.${key}`] = value;
        }

        onMultiFieldChange(additionalClientNewValues);
    };

    const hasAdditionalClient = !!client.additionalClient;

    const clientBasicInfo = {
        id: client.id,
        name: client.name,
        preferredName: client.preferredName,
        email: client.email,
        phone: client.phone,
        cellPhone: client.cellPhone,
        birthday: client.birthday,
        isBirthdayFake: client.isBirthdayFake,
        gender: client.gender,
        weight: client.weight,
        height: client.height,
        maritalStatus: client.maritalStatus,
    };

    const title = "Client" + (hasAdditionalClient ? "s" : "");

    return (
        <InputFormBlock>
            <ClientInfoHeader title={title} showIcon isCouple={hasAdditionalClient}>
                {!settings.showClientFirst && (
                    <div>
                        <Checkbox
                            onChange={e =>
                                sameAsBestContactCheckboxChanged(e.target.checked)
                            }
                            checked={client.clientSameAsBestContact}
                            label="Resident is same as Best Contact"
                        />
                    </div>
                )}
            </ClientInfoHeader>
            <PrimaryClientInfo
                clientBasicInfo={clientBasicInfo}
                clientSameAsBestContact={client.clientSameAsBestContact}
                onFieldChange={onFieldChange}
                onMultiFieldChange={onMultiFieldChange}
                showClientIntakeHeight={settings.showClientIntakeHeight}
                showClientIntakeWeight={settings.showClientIntakeWeight}
                now={now}
                emailOptOut={client.emailOptOut}
                hasAdditionalClient={hasAdditionalClient}
            />
            <div className="mb-5" />
            {hasAdditionalClient ? (
                <AdditionalClientInfo
                    clientInfo={client.additionalClient}
                    clientId={client.id}
                    clientSameAsBestContact={client.clientSameAsBestContact}
                    showClientIntakeWeight={settings.showClientIntakeWeight}
                    showClientIntakeHeight={settings.showClientIntakeHeight}
                    emailOptOut={client.emailOptOut}
                    now={now}
                    onFieldChange={onAdditionalClientFieldChange}
                    onMultiFieldChange={onAdditionalClientMultiFieldChange}
                    onDeleteAdditionalClient={onDeleteAdditionalClient}
                />
            ) : (
                <SecondaryButton onClick={onCreateNewAdditionalClient}>
                    Add another client
                </SecondaryButton>
            )}
        </InputFormBlock>
    );
};
